import { useParams } from '@vkontakte/vk-mini-apps-router';
import type { NavIdProps } from '@vkontakte/vkui';
import { useState } from 'react';
import { api, errorMessage } from '../../api/client';
import { useLoader } from '../../api/loader';
import { useSession } from '../../api/session';
import { useBack } from '../../app/useBack';
import { t } from '../../i18n';
import { LinkIcon, PencilIcon, TrashIcon } from '../../shared/icons';
import { docNumber } from '../../shared/lib/docNumber';
import {
  Action,
  ActionRow,
  Card,
  ErrorState,
  Gap,
  Gutter,
  Hero,
  LinkCard,
  LoadingState,
  Muted,
  Prose,
  ReadMore,
  Screen,
  Section,
  SectionTitle,
  toast,
} from '../../shared/ui';
import { canEditFaceClaims, FaceClaimEdit, type FaceClaimItem } from './FaceClaimEdit';

export function FaceClaimPanel({ id }: NavIdProps) {
  const session = useSession();
  const goBack = useBack();
  const params = useParams<'faceClaimId'>();
  const claimId = Number(params?.faceClaimId);
  const canEdit = canEditFaceClaims(session);

  const valid = Number.isFinite(claimId) && claimId > 0;
  const claim = useLoader(
    (signal) => api.get<FaceClaimItem>(`/api/face-claims/${claimId}`, undefined, signal),
    [claimId],
    valid,
  );
  // Уходящая панель ещё рисуется с параметрами нового маршрута, и номер уже
  // NaN: прежнюю карточку держим до конца анимации. Ошибка без номера — только
  // если карточки не было вовсе, то есть открыли кривой адрес.
  const card = valid ? (claim.data?.id === claimId ? claim.data : null) : claim.data;
  const error = valid ? (claim.error ?? '') : card === null ? t('face.error.noFace') : '';

  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Правка и вопрос об удалении — про ту внешку, что была открыта: на соседнюю
  // они не переезжают.
  // Сравниваем сырую строку, а не число: NaN не равен сам себе, и сброс по нему
  // крутил бы рендер без конца.
  const rawId = params?.faceClaimId ?? '';
  const [shownId, setShownId] = useState(rawId);
  if (shownId !== rawId) {
    setShownId(rawId);
    setEditing(false);
    setConfirming(false);
  }

  const remove = async () => {
    setDeleting(true);
    try {
      await api.delete<{ ok: true }>(`/api/face-claims/${claimId}`);
      // Список перечитает себя сам, когда снова станет активной панелью.
      back();
    } catch (cause: unknown) {
      setDeleting(false);
      setConfirming(false);
      toast(errorMessage(cause));
    }
  };

  const back = () => {
    if (editing) {
      setEditing(false);
      return;
    }
    goBack();
  };

  /*
    Шапка нужна только правке: у карточки кадр начинается от верхнего
    края, а возврат лежит поверх него — так нарисовано в холсте. Нет
    названия — нет и шапки, поэтому условие живёт прямо в `title`.
  */
  return (
    <Screen id={id} title={editing ? t('face.screen.edit') : undefined} back={back}>
      {claim.loading ? <LoadingState text={t('face.loading.card')} /> : null}

      {!claim.loading && !card ? (
        <ErrorState message={error} title={t('face.error.cardFailed')} onRetry={claim.reload} />
      ) : null}

      {card && editing ? (
        <FaceClaimEdit
          initial={card}
          canUploadPhoto={session.user?.canWrite === true}
          onCancel={() => setEditing(false)}
          onSaved={(saved) => {
            claim.replace(saved);
            setEditing(false);
            toast(t('face.saved.updated'));
          }}
        />
      ) : null}

      {card && !editing ? (
        <>
          {/* Шапка та же, что у досье персонажа: кадр от края до края и имя
              поверх него. Ради кадра в справочник и заходят, поэтому он идёт
              целиком, без обрезки. Просмотр на весь экран живёт здесь, а не в
              плитке списка: там Thumb оказался бы кнопкой внутри кнопки.
              Заглушки вместо фото нет — пустой квадрат в полэкрана говорит
              меньше, чем строка про карточку с одним именем. Шапки над кадром
              тоже нет, и в раздел он не завёрнут: в холсте фотография
              начинается от верхнего края экрана, а возврат лежит на ней. */}
          <Hero
            image={card.image}
            ratio="natural"
            caption={t('face.card.caption', { doc: docNumber(card.id) })}
            title={card.name}
            onBack={back}
          />

          {card.link ? (
            <Section>
              <SectionTitle accent="cyan">{t('common.field.source')}</SectionTitle>
              <Gutter>
                {/* Нажимается вся карточка, а не одна подпись: адрес — одно
                    длинное слово, и попасть по нему пальцем трудно. Значок
                    цепи говорит, что уход внешний, раньше чем прочтён адрес. */}
                <LinkCard href={card.link} icon={<LinkIcon size={15} tone="accent" />}>
                  {card.link}
                </LinkCard>
              </Gutter>
            </Section>
          ) : null}

          {card.note ? (
            <Section>
              <SectionTitle>{t('common.field.note')}</SectionTitle>
              <Gutter>
                <Card>
                  <ReadMore>
                    <Prose>{card.note}</Prose>
                  </ReadMore>
                </Card>
              </Gutter>
            </Section>
          ) : null}

          {!card.link && !card.note && !card.image ? (
            <Section>
              <Gutter>
                <Muted>{t('face.empty.card')}</Muted>
              </Gutter>
            </Section>
          ) : null}

          {canEdit ? (
            <Section>
              {/* Малиновый луч тут единственный на экране: он отделяет то,
                  что доступно персоналу, от самой карточки. */}
              <SectionTitle accent="pink">{t('face.card.staff')}</SectionTitle>
              <Gutter>
                {confirming ? (
                  <>
                    {/* Своего попаута у панели нет, поэтому подтверждение живёт
                        прямо в потоке экрана: удаление здесь безвозвратное. */}
                    <Prose>{t('face.remove.confirm', { name: card.name })}</Prose>
                    <Gap step="s3" />
                    <ActionRow>
                      <Action
                        tone="action"
                        size="m"
                        stretched
                        loading={deleting}
                        onClick={() => {
                          void remove();
                        }}
                      >
                        {t('face.action.delete')}
                      </Action>
                      <Action tone="quiet" size="m" stretched onClick={() => setConfirming(false)}>
                        {t('common.action.cancel')}
                      </Action>
                    </ActionRow>
                  </>
                ) : (
                  // Правка забирает полосу, снос остаётся узкой кнопкой рядом:
                  // равные половины звали нажать одинаково охотно.
                  <ActionRow>
                    <Action
                      tone="accent"
                      size="m"
                      stretched
                      before={<PencilIcon />}
                      onClick={() => setEditing(true)}
                    >
                      {t('face.action.edit')}
                    </Action>
                    <Action
                      tone="danger"
                      size="m"
                      before={<TrashIcon />}
                      onClick={() => setConfirming(true)}
                    >
                      {t('face.action.delete')}
                    </Action>
                  </ActionRow>
                )}
              </Gutter>
            </Section>
          ) : null}
        </>
      ) : null}
    </Screen>
  );
}
